/**
 * SEARCH-002 — Sous-hook « données » de la palette (⌘K).
 *
 * Extrait mécaniquement de `SearchPalette.tsx` / `useSearchPalette` (aucun
 * changement de logique) : types recherchables/scopables, requête réseau,
 * normalisation des hits, groupes, meilleur résultat, cible d'aperçu et requêtes
 * d'aperçu. Appelé INCONDITIONNELLEMENT par `useSearchPalette` (ordre des hooks
 * préservé).
 */

import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useSearchPreview, type PreviewTarget } from '@/hooks/useSearchPreview'
import { queryKeys } from '@/lib/queryKeys'
import {
    CATEGORY_REGISTRY,
    SCOPE_FILTER_TYPES,
    searchableTypesFor,
    type SearchHit,
    type SourceType,
} from '@/lib/searchCategories'
import { groupHitsByRelevance } from '@/lib/searchGrouping'
import { estUnDelaiDeRecherche, IDENTIFIER_FIELDS } from './searchPaletteHelpers'
import { chercherPartout, saisieCherchable, type RechercheGlobale } from './rechercheGlobale'

export interface UseSearchPaletteDataArgs {
    active: boolean
    isOpen: boolean
    debouncedQ: string
    entityId: string | null
    scopeType: SourceType | null
    selectedValue: string
    hasPermission: (slug: string) => boolean
}

export function useSearchPaletteData({
    active,
    isOpen,
    debouncedQ,
    entityId,
    scopeType,
    selectedValue,
    hasPermission,
}: UseSearchPaletteDataArgs) {
    const searchableTypes = useMemo(() => searchableTypesFor(hasPermission), [hasPermission])
    const scopableTypes = useMemo(
        () => SCOPE_FILTER_TYPES.filter((type) => searchableTypes.includes(type)),
        [searchableTypes],
    )
    const queryTypes = useMemo(
        () => (scopeType ? searchableTypes.filter((t) => t === scopeType) : searchableTypes),
        [scopeType, searchableTypes],
    )

    const enabled = active && isOpen && saisieCherchable(debouncedQ) && queryTypes.length > 0
    const { data, isLoading, isError, error, refetch } = useQuery<RechercheGlobale>({
        queryKey: queryKeys.search.global(debouncedQ, queryTypes),
        queryFn: ({ signal }) => chercherPartout({ q: debouncedQ, entityId, types: queryTypes, signal }),
        enabled,
        placeholderData: keepPreviousData,
        retry: false,
        staleTime: 30_000,
    })
    // Délai du serveur dépassé (`search_timeout`) : un message à part, qui dit de préciser.
    const isTimeout = isError && estUnDelaiDeRecherche(error)
    // Correction des fautes abandonnée faute de temps : les résultats sont ceux trouvés
    // SANS faute, il en manque peut-être — la palette le dit sous la liste.
    const typosSkipped = data?.typosSkipped === true

    const hits: SearchHit[] = useMemo(
        () =>
            (data?.results ?? []).map((h) => ({
                type: h.result_type,
                id: h.id,
                label: h.label,
                secondary: h.secondary,
                matchedField: h.matched_field,
                entityId: h.entity_id,
                organizationId: h.organization_id,
                originType: h.origin_type,
                originId: h.origin_id,
                avatarUrl: h.avatar_url,
            })),
        [data],
    )

    const bestHit = useMemo(() => {
        const first = hits[0]
        if (first && first.matchedField && IDENTIFIER_FIELDS.has(first.matchedField)) return first
        return null
    }, [hits])

    // Ordre des groupes = pertinence serveur (helper partagé avec le sélecteur
    // « Fiche liée ») ; le « meilleur résultat », déjà affiché en tête, est retiré.
    const groups = useMemo(
        () =>
            groupHitsByRelevance(
                hits,
                bestHit ? (h) => h.type === bestHit.type && h.id === bestHit.id : undefined,
            ),
        [hits, bestHit],
    )

    const previewTarget: PreviewTarget | null = useMemo(() => {
        const sep = selectedValue.indexOf(':')
        if (sep < 0) return null
        // cmdk NORMALISE (passe en minuscules) la valeur remontée par
        // `onValueChange` ; or les TypeID contiennent des MAJUSCULES (payload
        // base32, ex. `cli_01H2X5…`). Reconstruire l'id depuis `selectedValue`
        // donnerait donc un id en minuscules → aperçu 404 + bouton « Ouvrir »
        // du panneau inopérant (`hits.find` ne matche plus). On retrouve le hit
        // d'origine par comparaison insensible à la casse et on renvoie ses
        // `type`/`id` EXACTS.
        const match = hits.find(
            (h) => `${h.type}:${h.id}`.toLowerCase() === selectedValue.toLowerCase(),
        )
        if (match) return { type: match.type, id: match.id }
        // Repli : valeurs non-hit (`recent-q:`, `action:`, `scope:`, `nr:create:`…)
        // — pas des TypeID, la casse est neutre.
        const type = selectedValue.slice(0, sep) as SourceType
        const id = selectedValue.slice(sep + 1)
        if (!CATEGORY_REGISTRY[type] || !id) return null
        return { type, id }
    }, [selectedValue, hits])

    const desktopPreviewQuery = useSearchPreview(previewTarget, entityId)

    return {
        searchableTypes,
        scopableTypes,
        queryTypes,
        enabled,
        data,
        isLoading,
        isError,
        isTimeout,
        typosSkipped,
        refetch,
        hits,
        bestHit,
        groups,
        previewTarget,
        desktopPreviewQuery,
    }
}
